/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { DeliveryComment } from '../models/delivery-comment';
import { DeliveryCommentUpsert } from '../models/delivery-comment-upsert';
import { DeliveriesPlanning } from '../models/deliveries-planning';
import { DeliveriesPlanningState } from '../models/deliveries-planning-state';
import { DeliveryList } from '../models/delivery-list';
import { DeliverySearchOptions } from '../models/delivery-search-options';
import { DeliveryCreate } from '../models/delivery-create';
import { DeliveriesDelete } from '../models/deliveries-delete';
import { DeliveryExportOptions } from '../models/delivery-export-options';
import { DeliveriesPublish } from '../models/deliveries-publish';
import { DeliveriesDelivered } from '../models/deliveries-delivered';
import { Delivery } from '../models/delivery';
import { DeliveryUpdate } from '../models/delivery-update';
import { DeliveryDriverValidate } from '../models/delivery-driver-validate';
import { DeliverySplit } from '../models/delivery-split';
import { DeliveryPatch } from '../models/delivery-patch';
import { DeliveryPatchUpsert } from '../models/delivery-patch-upsert';
import { DeliveryRoadmap } from '../models/delivery-roadmap';

/**
 * All about the logistics
 */
@Injectable({
  providedIn: 'root',
})
class LogisticsService extends __BaseService {
  static readonly createDeliveryCommentPath = '/deliveries/{uid}/comments';
  static readonly editDeliveryCommentPath = '/deliveries/{uid}/comments/{deliveryCommentUid}';
  static readonly deleteDeliveryCommentPath = '/deliveries/{uid}/comments/{deliveryCommentUid}';
  static readonly retrieveDeliveriesPlanningPath = '/deliveries/planning';
  static readonly retrieveDeliveriesPlanningStatePath = '/deliveries/planning/state';
  static readonly searchDeliveriesPath = '/deliveries/search';
  static readonly createDeliveryRequestPath = '/deliveries';
  static readonly deleteDeliveriesPath = '/deliveries';
  static readonly exportDeliveriesPath = '/deliveries/export';
  static readonly publishDeliveriesPath = '/deliveries/publish';
  static readonly deliverDeliveriesPath = '/deliveries/deliver';
  static readonly retrieveDeliveryRequestPath = '/deliveries/{uid}';
  static readonly updateDeliveryRequestPath = '/deliveries/{uid}';
  static readonly deleteDeliveryRequestPath = '/deliveries/{uid}';
  static readonly deliveryDriverValidationPath = '/deliveries/{uid}/validate';
  static readonly retrieveDeliverySplitPath = '/deliveries/{uid}/split';
  static readonly splitDeliveryRequestPath = '/deliveries/{uid}/split';
  static readonly retrieveDeliveryPatchPath = '/deliveries/{uid}/patch';
  static readonly upsertDeliveryPatchPath = '/deliveries/{uid}/patch';
  static readonly deleteDeliveryPatchPath = '/deliveries/{uid}/patch';
  static readonly retrieveDeliveryRoadMapPath = '/deliveries/roadmap';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Create a delivery comment
   *
   * Create a delivery comment
   * @param params The `LogisticsService.CreateDeliveryCommentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `delivery comment`: The delivery comment
   *
   * @return Created
   */
  createDeliveryCommentResponse(params: LogisticsService.CreateDeliveryCommentParams): __Observable<__StrictHttpResponse<DeliveryComment>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.deliveryComment;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/comments`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveryComment>;
      })
    );
  }
  /**
   * Create a delivery comment
   *
   * Create a delivery comment
   * @param params The `LogisticsService.CreateDeliveryCommentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `delivery comment`: The delivery comment
   *
   * @return Created
   */
  createDeliveryComment(params: LogisticsService.CreateDeliveryCommentParams): __Observable<DeliveryComment> {
    return this.createDeliveryCommentResponse(params).pipe(
      __map(_r => _r.body as DeliveryComment)
    );
  }

  /**
   * Edit a delivery comment
   *
   * Edit a delivery comment
   * @param params The `LogisticsService.EditDeliveryCommentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryCommentUid`: The object ID. Must be a valid ID.
   *
   * - `delivery comment`: The delivery comment
   *
   * @return Success
   */
  editDeliveryCommentResponse(params: LogisticsService.EditDeliveryCommentParams): __Observable<__StrictHttpResponse<DeliveryComment>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    __body = params.deliveryComment;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/comments/${encodeURIComponent(String(params.deliveryCommentUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveryComment>;
      })
    );
  }
  /**
   * Edit a delivery comment
   *
   * Edit a delivery comment
   * @param params The `LogisticsService.EditDeliveryCommentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryCommentUid`: The object ID. Must be a valid ID.
   *
   * - `delivery comment`: The delivery comment
   *
   * @return Success
   */
  editDeliveryComment(params: LogisticsService.EditDeliveryCommentParams): __Observable<DeliveryComment> {
    return this.editDeliveryCommentResponse(params).pipe(
      __map(_r => _r.body as DeliveryComment)
    );
  }

  /**
   * Delete a delivery comment
   *
   * Delete a delivery comment
   * @param params The `LogisticsService.DeleteDeliveryCommentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryCommentUid`: The object ID. Must be a valid ID.
   */
  deleteDeliveryCommentResponse(params: LogisticsService.DeleteDeliveryCommentParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/comments/${encodeURIComponent(String(params.deliveryCommentUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a delivery comment
   *
   * Delete a delivery comment
   * @param params The `LogisticsService.DeleteDeliveryCommentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryCommentUid`: The object ID. Must be a valid ID.
   */
  deleteDeliveryComment(params: LogisticsService.DeleteDeliveryCommentParams): __Observable<null> {
    return this.deleteDeliveryCommentResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve the deliveries planning
   *
   * Retrieve the deliveries planning
   * @param params The `LogisticsService.RetrieveDeliveriesPlanningParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * @return Success
   */
  retrieveDeliveriesPlanningResponse(params: LogisticsService.RetrieveDeliveriesPlanningParams): __Observable<__StrictHttpResponse<DeliveriesPlanning>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.startDate != null) __params = __params.set('startDate', params.startDate.toString());
    if (params.endDate != null) __params = __params.set('endDate', params.endDate.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/planning`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveriesPlanning>;
      })
    );
  }
  /**
   * Retrieve the deliveries planning
   *
   * Retrieve the deliveries planning
   * @param params The `LogisticsService.RetrieveDeliveriesPlanningParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * @return Success
   */
  retrieveDeliveriesPlanning(params: LogisticsService.RetrieveDeliveriesPlanningParams): __Observable<DeliveriesPlanning> {
    return this.retrieveDeliveriesPlanningResponse(params).pipe(
      __map(_r => _r.body as DeliveriesPlanning)
    );
  }

  /**
   * Retrieve the deliveries planning state of recalculation
   *
   * Retrieve the deliveries planning state of recalculation
   * @return Success
   */
  retrieveDeliveriesPlanningStateResponse(): __Observable<__StrictHttpResponse<DeliveriesPlanningState>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/planning/state`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveriesPlanningState>;
      })
    );
  }
  /**
   * Retrieve the deliveries planning state of recalculation
   *
   * Retrieve the deliveries planning state of recalculation
   * @return Success
   */
  retrieveDeliveriesPlanningState(): __Observable<DeliveriesPlanningState> {
    return this.retrieveDeliveriesPlanningStateResponse().pipe(
      __map(_r => _r.body as DeliveriesPlanningState)
    );
  }

  /**
   * Search deliveries, without pagination
   *
   * Search deliveries, without pagination
   * @param params The `LogisticsService.SearchDeliveriesParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * DATE : the delivery's date
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The delivery search options object
   *
   * @return Success
   */
  searchDeliveriesResponse(params: LogisticsService.SearchDeliveriesParams): __Observable<__StrictHttpResponse<DeliveryList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.startDate != null) __params = __params.set('startDate', params.startDate.toString());
    if (params.endDate != null) __params = __params.set('endDate', params.endDate.toString());
    if (params.sortFieldName != null) __params = __params.set('sortFieldName', params.sortFieldName.toString());
    if (params.sortDirection != null) __params = __params.set('sortDirection', params.sortDirection.toString());
    __body = params.search;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveryList>;
      })
    );
  }
  /**
   * Search deliveries, without pagination
   *
   * Search deliveries, without pagination
   * @param params The `LogisticsService.SearchDeliveriesParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * DATE : the delivery's date
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The delivery search options object
   *
   * @return Success
   */
  searchDeliveries(params: LogisticsService.SearchDeliveriesParams): __Observable<DeliveryList> {
    return this.searchDeliveriesResponse(params).pipe(
      __map(_r => _r.body as DeliveryList)
    );
  }

  /**
   * Create a delivery request
   *
   * Create a delivery request
   * @param deliveryCreate The delivery request create object
   */
  createDeliveryRequestResponse(deliveryCreate: DeliveryCreate): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = deliveryCreate;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Create a delivery request
   *
   * Create a delivery request
   * @param deliveryCreate The delivery request create object
   */
  createDeliveryRequest(deliveryCreate: DeliveryCreate): __Observable<null> {
    return this.createDeliveryRequestResponse(deliveryCreate).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete deliveries
   *
   * Delete deliveries
   * @param deliveriesDelete The deliveries delete object
   */
  deleteDeliveriesResponse(deliveriesDelete: DeliveriesDelete): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = deliveriesDelete;
    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/deliveries`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete deliveries
   *
   * Delete deliveries
   * @param deliveriesDelete The deliveries delete object
   */
  deleteDeliveries(deliveriesDelete: DeliveriesDelete): __Observable<null> {
    return this.deleteDeliveriesResponse(deliveriesDelete).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Export deliveries
   *
   * Export deliveries
   * @param params The `LogisticsService.ExportDeliveriesParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * - `deliveriesExport`: The deliveries export options object
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * DATE : the delivery's date
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * @return Success
   */
  exportDeliveriesResponse(params: LogisticsService.ExportDeliveriesParams): __Observable<__StrictHttpResponse<Blob>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.startDate != null) __params = __params.set('startDate', params.startDate.toString());
    if (params.endDate != null) __params = __params.set('endDate', params.endDate.toString());
    __body = params.deliveriesExport;
    if (params.sortFieldName != null) __params = __params.set('sortFieldName', params.sortFieldName.toString());
    if (params.sortDirection != null) __params = __params.set('sortDirection', params.sortDirection.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/export`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'blob'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Blob>;
      })
    );
  }
  /**
   * Export deliveries
   *
   * Export deliveries
   * @param params The `LogisticsService.ExportDeliveriesParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * - `deliveriesExport`: The deliveries export options object
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * DATE : the delivery's date
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * @return Success
   */
  exportDeliveries(params: LogisticsService.ExportDeliveriesParams): __Observable<Blob> {
    return this.exportDeliveriesResponse(params).pipe(
      __map(_r => _r.body as Blob)
    );
  }

  /**
   * Publish draft deliveries
   *
   * Publish draft deliveries.
   * This will apply the deliveries' patches
   * @param deliveriesPublish The deliveries publish object
   */
  publishDeliveriesResponse(deliveriesPublish: DeliveriesPublish): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = deliveriesPublish;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/publish`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Publish draft deliveries
   *
   * Publish draft deliveries.
   * This will apply the deliveries' patches
   * @param deliveriesPublish The deliveries publish object
   */
  publishDeliveries(deliveriesPublish: DeliveriesPublish): __Observable<null> {
    return this.publishDeliveriesResponse(deliveriesPublish).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Deliver draft deliveries
   *
   * Deliver draft deliveries.
   * This will delete the deliveries' patches
   * @param deliveriesDelivered The deliveries delivered object
   */
  deliverDeliveriesResponse(deliveriesDelivered: DeliveriesDelivered): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = deliveriesDelivered;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/deliver`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Deliver draft deliveries
   *
   * Deliver draft deliveries.
   * This will delete the deliveries' patches
   * @param deliveriesDelivered The deliveries delivered object
   */
  deliverDeliveries(deliveriesDelivered: DeliveriesDelivered): __Observable<null> {
    return this.deliverDeliveriesResponse(deliveriesDelivered).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve a delivery request
   *
   * Retrieve a delivery request
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveDeliveryRequestResponse(uid: string): __Observable<__StrictHttpResponse<Delivery>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Delivery>;
      })
    );
  }
  /**
   * Retrieve a delivery request
   *
   * Retrieve a delivery request
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveDeliveryRequest(uid: string): __Observable<Delivery> {
    return this.retrieveDeliveryRequestResponse(uid).pipe(
      __map(_r => _r.body as Delivery)
    );
  }

  /**
   * Update a delivery request
   *
   * Update a delivery request.
   * This will delete the delivery's patch
   * @param params The `LogisticsService.UpdateDeliveryRequestParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryUpdate`: The delivery request update object
   */
  updateDeliveryRequestResponse(params: LogisticsService.UpdateDeliveryRequestParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.deliveryUpdate;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Update a delivery request
   *
   * Update a delivery request.
   * This will delete the delivery's patch
   * @param params The `LogisticsService.UpdateDeliveryRequestParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryUpdate`: The delivery request update object
   */
  updateDeliveryRequest(params: LogisticsService.UpdateDeliveryRequestParams): __Observable<null> {
    return this.updateDeliveryRequestResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete a delivery request
   *
   * Delete a delivery request
   * @param uid The object ID. Must be a valid ID.
   */
  deleteDeliveryRequestResponse(uid: string): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a delivery request
   *
   * Delete a delivery request
   * @param uid The object ID. Must be a valid ID.
   */
  deleteDeliveryRequest(uid: string): __Observable<null> {
    return this.deleteDeliveryRequestResponse(uid).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Driver validation of a delivery request
   *
   * Driver validation of a delivery request
   * @param params The `LogisticsService.DeliveryDriverValidationParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryDriverValidate`: The delivery driver validate object
   */
  deliveryDriverValidationResponse(params: LogisticsService.DeliveryDriverValidationParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.deliveryDriverValidate;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/validate`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Driver validation of a delivery request
   *
   * Driver validation of a delivery request
   * @param params The `LogisticsService.DeliveryDriverValidationParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryDriverValidate`: The delivery driver validate object
   */
  deliveryDriverValidation(params: LogisticsService.DeliveryDriverValidationParams): __Observable<null> {
    return this.deliveryDriverValidationResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve a main delivery with its children
   *
   * Retrieve a main delivery with its children
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveDeliverySplitResponse(uid: string): __Observable<__StrictHttpResponse<DeliverySplit>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(uid))}/split`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliverySplit>;
      })
    );
  }
  /**
   * Retrieve a main delivery with its children
   *
   * Retrieve a main delivery with its children
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveDeliverySplit(uid: string): __Observable<DeliverySplit> {
    return this.retrieveDeliverySplitResponse(uid).pipe(
      __map(_r => _r.body as DeliverySplit)
    );
  }

  /**
   * Split a delivery request ('depotage' in french), update if a split already exists
   *
   * Split a delivery request ('depotage' in french), update if a split already exists.
   * This will delete the children deliveries' patches
   * @param params The `LogisticsService.SplitDeliveryRequestParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliverySplit`: The delivery split object
   */
  splitDeliveryRequestResponse(params: LogisticsService.SplitDeliveryRequestParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.deliverySplit;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/split`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Split a delivery request ('depotage' in french), update if a split already exists
   *
   * Split a delivery request ('depotage' in french), update if a split already exists.
   * This will delete the children deliveries' patches
   * @param params The `LogisticsService.SplitDeliveryRequestParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliverySplit`: The delivery split object
   */
  splitDeliveryRequest(params: LogisticsService.SplitDeliveryRequestParams): __Observable<null> {
    return this.splitDeliveryRequestResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve a delivery's patch
   *
   * Retrieve a published delivery's modifications that have not yet been republished (if any)
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveDeliveryPatchResponse(uid: string): __Observable<__StrictHttpResponse<DeliveryPatch>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(uid))}/patch`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveryPatch>;
      })
    );
  }
  /**
   * Retrieve a delivery's patch
   *
   * Retrieve a published delivery's modifications that have not yet been republished (if any)
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveDeliveryPatch(uid: string): __Observable<DeliveryPatch> {
    return this.retrieveDeliveryPatchResponse(uid).pipe(
      __map(_r => _r.body as DeliveryPatch)
    );
  }

  /**
   * Upsert a delivery's patch
   *
   * Create or update a published delivery's modifications
   * @param params The `LogisticsService.UpsertDeliveryPatchParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryPatchUpsert`: A delivery patch upsert object
   */
  upsertDeliveryPatchResponse(params: LogisticsService.UpsertDeliveryPatchParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.deliveryPatchUpsert;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/patch`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Upsert a delivery's patch
   *
   * Create or update a published delivery's modifications
   * @param params The `LogisticsService.UpsertDeliveryPatchParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `deliveryPatchUpsert`: A delivery patch upsert object
   */
  upsertDeliveryPatch(params: LogisticsService.UpsertDeliveryPatchParams): __Observable<null> {
    return this.upsertDeliveryPatchResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete a delivery's patch
   *
   * Delete a published delivery's modifications
   * @param uid The object ID. Must be a valid ID.
   */
  deleteDeliveryPatchResponse(uid: string): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(uid))}/patch`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a delivery's patch
   *
   * Delete a published delivery's modifications
   * @param uid The object ID. Must be a valid ID.
   */
  deleteDeliveryPatch(uid: string): __Observable<null> {
    return this.deleteDeliveryPatchResponse(uid).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve the roadmap of deliveries
   *
   * Retrieve the roadmap of deliveries
   * @param params The `LogisticsService.RetrieveDeliveryRoadMapParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * @return Success
   */
  retrieveDeliveryRoadMapResponse(params: LogisticsService.RetrieveDeliveryRoadMapParams): __Observable<__StrictHttpResponse<Array<DeliveryRoadmap>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.startDate != null) __params = __params.set('startDate', params.startDate.toString());
    if (params.endDate != null) __params = __params.set('endDate', params.endDate.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/roadmap`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<DeliveryRoadmap>>;
      })
    );
  }
  /**
   * Retrieve the roadmap of deliveries
   *
   * Retrieve the roadmap of deliveries
   * @param params The `LogisticsService.RetrieveDeliveryRoadMapParams` containing the following parameters:
   *
   * - `startDate`: Start date
   *
   * - `endDate`: End date
   *
   * @return Success
   */
  retrieveDeliveryRoadMap(params: LogisticsService.RetrieveDeliveryRoadMapParams): __Observable<Array<DeliveryRoadmap>> {
    return this.retrieveDeliveryRoadMapResponse(params).pipe(
      __map(_r => _r.body as Array<DeliveryRoadmap>)
    );
  }
}

namespace LogisticsService {

  /**
   * Parameters for createDeliveryComment
   */
  export interface CreateDeliveryCommentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The delivery comment
     */
    deliveryComment: DeliveryCommentUpsert;
  }

  /**
   * Parameters for editDeliveryComment
   */
  export interface EditDeliveryCommentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    deliveryCommentUid: string;

    /**
     * The delivery comment
     */
    deliveryComment: DeliveryCommentUpsert;
  }

  /**
   * Parameters for deleteDeliveryComment
   */
  export interface DeleteDeliveryCommentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    deliveryCommentUid: string;
  }

  /**
   * Parameters for retrieveDeliveriesPlanning
   */
  export interface RetrieveDeliveriesPlanningParams {

    /**
     * Start date
     */
    startDate: string;

    /**
     * End date
     */
    endDate: string;
  }

  /**
   * Parameters for searchDeliveries
   */
  export interface SearchDeliveriesParams {

    /**
     * Start date
     */
    startDate: string;

    /**
     * End date
     */
    endDate: string;

    /**
     * Indicates the field to use for sorting the data:
     *   * DATE : the delivery's date
     */
    sortFieldName?: 'DATE';

    /**
     * Sort direction : ASC, DESC
     */
    sortDirection?: 'ASC' | 'DESC';

    /**
     * The delivery search options object
     */
    search?: DeliverySearchOptions;
  }

  /**
   * Parameters for exportDeliveries
   */
  export interface ExportDeliveriesParams {

    /**
     * Start date
     */
    startDate: string;

    /**
     * End date
     */
    endDate: string;

    /**
     * The deliveries export options object
     */
    deliveriesExport: DeliveryExportOptions;

    /**
     * Indicates the field to use for sorting the data:
     *   * DATE : the delivery's date
     */
    sortFieldName?: 'DATE';

    /**
     * Sort direction : ASC, DESC
     */
    sortDirection?: 'ASC' | 'DESC';
  }

  /**
   * Parameters for updateDeliveryRequest
   */
  export interface UpdateDeliveryRequestParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The delivery request update object
     */
    deliveryUpdate: DeliveryUpdate;
  }

  /**
   * Parameters for deliveryDriverValidation
   */
  export interface DeliveryDriverValidationParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The delivery driver validate object
     */
    deliveryDriverValidate: DeliveryDriverValidate;
  }

  /**
   * Parameters for splitDeliveryRequest
   */
  export interface SplitDeliveryRequestParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The delivery split object
     */
    deliverySplit: DeliverySplit;
  }

  /**
   * Parameters for upsertDeliveryPatch
   */
  export interface UpsertDeliveryPatchParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * A delivery patch upsert object
     */
    deliveryPatchUpsert: DeliveryPatchUpsert;
  }

  /**
   * Parameters for retrieveDeliveryRoadMap
   */
  export interface RetrieveDeliveryRoadMapParams {

    /**
     * Start date
     */
    startDate: string;

    /**
     * End date
     */
    endDate: string;
  }
}

export { LogisticsService }
